import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GeneralDataContext } from '../State/GeneralDataContext';
import { buildLanguageHintCopy } from '../utils/cmsCopy';
import './BrowserTranslateHint.css';

const DISMISS_KEY = 'sdg-translate-hint-dismissed';
const LOCALE_KEY = 'sdg-translate-hint-locale';

/** True for phones/tablets — not merely a narrow desktop window. */
function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS 13+ often reports as MacIntel but is touch-driven
  const iPadOsDesktopUa =
    navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints || 0) > 1;
  if (iPadOsDesktopUa) return true;
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua)) {
    return true;
  }
  // Fallback: coarse pointer + no hover ≈ phone/tablet UI
  try {
    if (
      window.matchMedia('(pointer: coarse)').matches &&
      window.matchMedia('(hover: none)').matches
    ) {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function detectPreferredLocale() {
  try {
    const saved = localStorage.getItem(LOCALE_KEY);
    if (saved === 'sq' || saved === 'sr') return saved;
  } catch {
    /* ignore */
  }
  try {
    const langs = navigator.languages?.length
      ? navigator.languages
      : [navigator.language || ''];
    for (const l of langs) {
      const code = String(l).toLowerCase().split('-')[0];
      if (code === 'sq') return 'sq';
      if (code === 'sr') return 'sr';
    }
  } catch {
    /* ignore */
  }
  return 'sq';
}

/**
 * Honest language affordance: site is English; guide users to browser translate.
 * Instructions in Albanian or Serbian (user-selected). CMS: Website General Data → Language menu.
 * Desktop vs mobile steps use device detection (UA / touch), not viewport width.
 * Opens only on user click — never on fresh page load.
 */
const BrowserTranslateHint = () => {
  const { data: generalData } = useContext(GeneralDataContext);
  const copy = useMemo(() => buildLanguageHintCopy(generalData), [generalData]);
  const isMobile = useMemo(() => isMobileDevice(), []);
  const [open, setOpen] = useState(false);
  const [locale, setLocale] = useState(() => detectPreferredLocale());
  const rootRef = useRef(null);
  const panelId = useId();

  const localeCopy = locale === 'sr' ? copy.sr : copy.sq;

  const steps = useMemo(
    () => [
      {
        key: 'chrome',
        label: localeCopy.chromeLabel,
        text: isMobile ? localeCopy.chromeMobile : localeCopy.chromeDesktop,
      },
      {
        key: 'safari',
        label: localeCopy.safariLabel,
        text: isMobile ? localeCopy.safariMobile : localeCopy.safariDesktop,
      },
      {
        key: 'firefox',
        label: localeCopy.firefoxLabel,
        text: isMobile ? localeCopy.firefoxMobile : localeCopy.firefoxDesktop,
      },
    ],
    [localeCopy, isMobile]
  );

  const selectLocale = useCallback((code) => {
    setLocale(code);
    try {
      localStorage.setItem(LOCALE_KEY, code);
    } catch {
      /* ignore */
    }
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const dismissTip = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setOpen(false);
  }, []);

  // Do not auto-open on first visit — only when the user clicks Language.
  // (DISMISS_KEY is still written when they click "Don't show again" for future use.)

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        close();
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  return (
    <div className="browser-translate-hint" ref={rootRef}>
      <button
        type="button"
        className="browser-translate-hint__trigger custom-contact-link"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={copy.menuLabel}
        onClick={() => setOpen((v) => !v)}
      >
        <i className="bi bi-globe2 browser-translate-hint__icon" aria-hidden="true" />
        <span>{copy.menuLabel}</span>
      </button>

      {open ? (
        <div
          id={panelId}
          className="browser-translate-hint__panel"
          role="dialog"
          aria-label={copy.menuLabel}
        >
          <div
            className="browser-translate-hint__langs"
            role="group"
            aria-label={copy.chooseLabel}
          >
            <div className="browser-translate-hint__langs-label">
              {copy.chooseLabel}
            </div>
            <button
              type="button"
              className={`browser-translate-hint__lang${locale === 'sq' ? ' is-active' : ''}`}
              aria-pressed={locale === 'sq'}
              onClick={() => selectLocale('sq')}
            >
              {copy.sq.optionLabel}
            </button>
            <button
              type="button"
              className={`browser-translate-hint__lang${locale === 'sr' ? ' is-active' : ''}`}
              aria-pressed={locale === 'sr'}
              onClick={() => selectLocale('sr')}
            >
              {copy.sr.optionLabel}
            </button>
          </div>

          <p className="browser-translate-hint__lead" lang={locale}>
            {localeCopy.lead}
          </p>

          <dl className="browser-translate-hint__steps" lang={locale}>
            {steps.map((step) => (
              <div key={step.key} className="browser-translate-hint__row">
                <dt>{step.label}</dt>
                <dd>{step.text}</dd>
              </div>
            ))}
          </dl>

          <div className="browser-translate-hint__actions">
            <button
              type="button"
              className="browser-translate-hint__dismiss"
              onClick={dismissTip}
            >
              {localeCopy.dismissLabel}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BrowserTranslateHint;
